/* eslint-disable @typescript-eslint/no-explicit-any */
import api from './api';
import { unwrapObject } from './api-envelope';
import type { B2BChannelType } from '@tcr-erp/shared';

const BASE = '/promotions';

export interface PromotionListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  type?: string;
  channel?: string;
  /**
   * TCR-2043 — chỉ có ý nghĩa khi `channel=wholesale`; lọc theo kênh B2B con
   * (SI/GT/MT). Không gửi khi rỗng/undefined.
   */
  b2b_channel?: string;
}

/**
 * TCR-2043 — phạm vi kênh B2B của user trên màn Khuyến mãi (GET
 * `/promotions/v2/my-scope`). `restricted: true` LUÔN kèm `channel` khác null
 * (BE: tài khoản "chỉ kênh mình" nhưng CHƯA gán kênh B2B thì coi như KHÔNG
 * giới hạn, khác với màn Bảng giá B2B — TCR-2017). Trên BE cũ (trước khi
 * deploy contract này) request rơi vào `v2/:id` và lỗi — caller phải coi
 * scope lỗi/rỗng là KHÔNG giới hạn (xem `usePromotionScope`).
 */
export interface PromotionViewScope {
  restricted: boolean;
  channel: B2BChannelType | null;
  /** TCR-2075 — mọi kênh của tài khoản (thiếu ⇒ rơi về `channel`). */
  channels?: B2BChannelType[];
}

export interface VoucherListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  voucher_type?: string;
  source?: string;
}

export interface ValidateVoucherPayload {
  code: string;
  /**
   * Fix round 1 F4: khai `EvaluateCartItem[]` (không phải `VoucherCartItem[]`
   * cũ, đã xoá) — FE build `items` bằng `buildEvaluateItems()` cho MỌI lời gọi
   * validate voucher, và hàm đó luôn gửi kèm `flash_sale_item_id`. Type cũ
   * thiếu field này khiến `tsc` im lặng cho qua (structural typing bỏ qua
   * excess property khi nguồn không phải object literal) dù payload thật sự
   * gửi field đó — trong khi BE bật `whitelist + forbidNonWhitelisted`, type
   * FE phải phản ánh đúng hình dạng payload thật.
   */
  items: EvaluateCartItem[];
  customer_id?: string;
  store_id?: string;
  channel?: string;
  /**
   * BE `PosPromotionVoucherValidateDto` nhận sẵn field này và
   * `condition-evaluator` chấm `ConditionType.PAYMENT_METHOD` bằng nó. FE
   * không gửi ⇒ voucher ràng buộc hình thức thanh toán luôn báo "chưa đủ
   * điều kiện" đúng lúc đơn đang dùng chính hình thức đó.
   */
  payment_method?: string;
}

export interface VoucherValidationResult {
  valid: boolean;
  reason?: string;
  voucher_id?: string;
  voucher_code?: string;
  voucher_name?: string;
  voucher_type?: string;
  discount_amount?: number;
  /** TCR-1074: có giá trị khi voucher loại "Tặng quà miễn phí". */
  gift_product_id?: string;
  gift_quantity?: number;
  gift_description?: string;
}

export interface EvaluateCartItem {
  product_id: string;
  /**
   * TCR-1369 — biến thể của dòng hàng. CTKM cấu hình theo biến thể chỉ khớp
   * đúng biến thể khi field này có mặt; vắng mặt thì engine rơi về khớp cấp
   * sản phẩm (kênh nào chưa gửi vẫn giữ nguyên hành vi cũ).
   */
  variant_id?: string;
  quantity: number;
  unit_price: number;
  flash_sale_item_id?: string;
  category_ids?: string[];
}

export interface EvaluateCartPayload {
  items: EvaluateCartItem[];
  customer_id?: string;
  store_id?: string;
  channel: 'pos' | 'online' | 'wholesale';
  /** TCR-1069: CTKM có `conditions.payment_methods` cần field này mới chấm đúng. */
  payment_method?: string;
  /** Phí vận chuyển hiện tại của đơn — CTKM freeship cần số này mới tính ra tiền. */
  shipping_fee?: number;
  fulfillment_mode?: 'delivery' | 'pickup' | 'vault';
}

export interface AppliedPromotion {
  promotion_id: string;
  name: string;
  type: string;
  discount_amount: number;
  applied_to: string;
  priority: number;
}

/**
 * TCR-1069 — CTKM đang chạy nhưng chưa áp được cho giỏ hàng hiện tại.
 *
 * `message` do BE sinh và hiển thị NGUYÊN VĂN. Không dựng lại câu chữ ở FE:
 * lý do phụ thuộc ngưỡng/điều kiện của từng CTKM, chép logic sang đây là bảo
 * đảm một ngày nào đó màn POS nói số cũ trong khi engine đã đổi.
 */
export interface IneligiblePromotion {
  promotion_id: string;
  code?: string;
  name: string;
  reasons: Array<{ code: string; message: string; values?: string[] }>;
}

export interface EvaluationResult {
  original_total: number;
  applied_promotions: AppliedPromotion[];
  /** Optional: snapshot cũ trong Redis ghi trước TCR-1069 không có field này. */
  ineligible_promotions?: IneligiblePromotion[];
  voucher_discount: number;
  loyalty_discount: number;
  /**
   * Khoản giảm PHÍ VẬN CHUYỂN do CTKM freeship. KHÔNG nằm trong
   * `total_discount` và không trừ vào `final_total` — phí ship không thuộc
   * tiền hàng; nó trừ ở bước cộng phí ship của `computePosMoney`.
   *
   * Optional ở biên: snapshot Redis ghi trước khi BE có field này không mang
   * nó, và bên đọc phải tự phòng thay vì để `undefined` trôi thành NaN.
   */
  shipping_discount?: number;
  total_discount: number;
  final_total: number;
  points_to_earn: number;
}

export interface PromotionCartEvaluationDto extends EvaluationResult {
  free_items: Array<{
    product_id: string;
    /**
     * TCR-1369 — biến thể ĐÍCH DANH mà CTKM tặng. Màn cấu hình CTKM cho chọn
     * biến thể; BE (`promotion-engine`) tách nó ra khỏi `product_id` trước khi
     * trả về, nên POS không phải đoán "biến thể nào trong sản phẩm này".
     * Vắng mặt = CTKM chỉ nêu sản phẩm, thu ngân chọn biến thể như cũ.
     */
    variant_id?: string;
    product_name?: string;
    quantity: number;
    value: number;
    source_promotion_id: string;
    /**
     * TCR-1074 (final review, Critical 2) — MỨC giảm của dòng "get" trong
     * CTKM Mua X tặng Y, [0..100]. `100` = tặng miễn phí.
     *
     * BXGY cho phép cấu hình cả các mức khác ("mua X, mua Y giảm 50%") và
     * engine tôn trọng chúng khi tính tiền, nhưng dòng quà mà POS dựng LUÔN
     * có `price: 0`. Vì thế POS chỉ dựng banner "nhận quà" cho entry
     * `discount_percent === 100` (xem `usePromotionGiftAdd.ts`) — các mức
     * khác im lặng không hiện, an toàn hơn nhiều so với phát không.
     *
     * Optional: snapshot khuyến mãi ghi vào Redis TRƯỚC bản vá BE tương ứng
     * không có field này ⇒ phải xử lý fail-closed, KHÔNG mặc định về 100.
     */
    discount_percent?: number;
  }>;
  flash_sale_items?: Array<{ item_id: string; quantity: number }>;
  snapshot_id?: string;
}

export interface PosPromotionListItem {
  promotion_id: string;
  code?: string;
  name: string;
  type: string;
  status?: string;
  schedule?: { start_date?: string; end_date?: string };
  conditions?: { customer_groups?: string[]; member_tiers?: string[] };
  actions?: Array<{ discount_percent?: number; discount_amount?: number }>;
}

export interface PosVoucherListItem {
  voucher_id: string;
  code: string;
  name: string;
  description?: string;
  voucher_type: string;
  discount_amount?: number;
  discount_percent?: number;
  valid_from?: string;
  valid_until?: string;
  status?: string;
}

export interface PosListResponse<T> {
  data: T[];
  pagination: {
    page?: number;
    limit?: number;
    page_size?: number;
    total?: number;
    total_pages?: number;
  };
  success?: boolean;
}

export interface PosLoyaltyProgram {
  program_id: string;
  name: string;
  is_active?: boolean;
  redemption?: {
    rate: number;
    min_points?: number;
    max_percent_of_order?: number;
  };
  points_expiry_months?: number;
  expiry_notification_days?: number;
}

export interface PosLoyaltyTier {
  tier_id: string;
  name: string;
  slug?: string;
  level?: number;
  color?: string;
  icon_url?: string;
  is_active?: boolean;
  sort_order?: number;
}

export interface PosCustomerTier {
  tier: PosLoyaltyTier | null;
  total_points_earned: number;
  next_tier: PosLoyaltyTier | null;
  points_to_next_tier: number;
}

interface PosObjectEnvelope<T> {
  data: T;
  success?: boolean;
}

function unwrapPosObject<T>(payload: PosObjectEnvelope<T> | T): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return payload.data as T;
  }
  return payload;
}

export interface FlashSaleListParams {
  page?: number;
  limit?: number;
  state?: string;
  search?: string;
  include_deleted?: boolean;
}

export interface WholesaleGiftListParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  q?: string;
}

export interface ReportParams {
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
}

export interface PromotionPayload {
  name: string;
  code?: string;
  type: string;
  description?: string;
  slug?: string;
  image_url?: string;
  store_ids?: string[];
  schedule: {
    start_date: string;
    end_date?: string;
    timezone?: string;
    recurring?: Record<string, unknown>;
  };
  actions: Array<{
    type: string;
    discount_percent?: number;
    discount_amount?: number;
    max_discount_amount?: number;
    free_product_id?: string;
    free_product_quantity?: number;
    target?: string;
    target_product_ids?: string[];
    target_category_ids?: string[];
  }>;
  /**
   * TCR-2043 — `channels` giữ nguyên 3 kênh lớn (pos/online/wholesale);
   * `b2b_channels` (SI/GT/MT) chỉ có ý nghĩa khi `channels` chứa 'wholesale',
   * rỗng/thiếu ⇒ áp dụng cả 3 kênh B2B (dữ liệu legacy). Vẫn giữ
   * `Record<string, unknown>` cho các key điều kiện khác (min_order_amount, ...).
   */
  conditions?: Record<string, unknown> & { channels?: string[]; b2b_channels?: string[] };
  limits?: Record<string, unknown>;
  stackable?: Record<string, unknown>;
  bxgy_config?: Record<string, unknown>;
  bundle_config?: Record<string, unknown>;
  tier_config?: Array<{ tier: string; discount_percent: number }>;
}

export interface VoucherPayload {
  code: string;
  name: string;
  voucher_type: string;
  description?: string;
  discount_amount?: number;
  discount_percent?: number;
  max_discount_amount?: number;
  gift_product_id?: string;
  gift_quantity?: number;
  gift_description?: string;
  source?: string;
  valid_from: string;
  valid_until?: string;
  total_limit?: number;
  max_uses_per_customer?: number;
  max_uses_per_day?: number;
  total_budget?: number;
  target_channels?: string[];
  target_store_ids?: string[];
  target_customer_ids?: string[];
  is_exclusive?: boolean;
  display_on_checkout?: boolean;
}

export interface FlashSalePayload {
  name: string;
  description?: string;
  banner_image_url?: string;
  start_time: string;
  end_time: string;
  timezone?: string;
  store_ids?: string[];
  channels?: string[];
  max_orders?: number;
  show_countdown?: boolean;
  show_stock_remaining?: boolean;
  featured?: boolean;
}

export interface AddFlashSaleItemPayload {
  product_id: string;
  variant_id?: string;
  original_price: number;
  flash_price?: number;
  discount_percent?: number;
  discount_mode?: 'percent' | 'amount';
  max_quantity: number;
  max_per_customer?: number;
  sort_order?: number;
  is_active?: boolean;
}

export interface UpdateFlashSaleItemPayload {
  flash_price?: number;
  discount_percent?: number;
  discount_mode?: 'percent' | 'amount';
  max_quantity?: number;
  max_per_customer?: number;
  is_active?: boolean;
  sort_order?: number;
}

export interface LoyaltyProgramPayload {
  is_active?: boolean;
  earning_rules?: Array<{
    id: string;
    name: string;
    event: string;
    points_per_currency: number;
    is_active?: boolean;
  }>;
  redemption?: {
    rate: number;
    min_points: number;
    max_percent_of_order?: number;
  };
  points_expiry_months?: number;
  expiry_notification_days?: number;
}

export interface LoyaltyTierPayload {
  name: string;
  slug: string;
  level: number;
  entry_threshold: {
    type: string;
    value: number;
    period?: string;
  };
  benefits?: {
    points_multiplier?: number;
    discount_percent?: number;
    free_shipping?: boolean;
    custom_benefits?: string[];
  };
  color?: string;
  icon_url?: string;
  is_active?: boolean;
  sort_order?: number;
}

export interface WholesaleGiftPayload {
  name: string;
  description?: string;
  gift_items: Array<{
    product_id: string;
    allocated_quantity: number;
  }>;
  trigger?: {
    type: string;
    min_amount?: number;
    min_quantity?: number;
    product_ids?: string[];
    category_ids?: string[];
    period?: string;
  };
  valid_from: string;
  valid_until: string;
  customer_id?: string;
  customer_group?: string;
  store_ids?: string[];
}

export interface ActiveFlashSaleItem {
  id: string;
  product_id: string;
  variant_id?: string;
  product_name?: string;
  variant_code?: string;
  original_price: number;
  flash_price?: number;
  discount_percent?: number;
  max_quantity: number;
  remaining_quantity: number;
  max_per_customer?: number;
}

export interface ActiveFlashSale {
  id: string;
  name: string;
  banner_image_url?: string;
  start_time: string;
  end_time: string;
  remaining_seconds: number;
  show_countdown: boolean;
  items: ActiveFlashSaleItem[];
}

export const promotionService = {
  pos: {
    listPromotions: (params: PromotionListParams): Promise<PosListResponse<PosPromotionListItem>> =>
      api.get<PosListResponse<PosPromotionListItem>>('/pos/promotions', { params }).then((r) => r.data),
    listVouchers: (params: VoucherListParams): Promise<PosListResponse<PosVoucherListItem>> =>
      api.get<PosListResponse<PosVoucherListItem>>('/pos/promotions/vouchers', { params }).then((r) => r.data),
    getActiveFlashSales: (storeId: string): Promise<ActiveFlashSale[]> =>
      api
        .get<PosObjectEnvelope<ActiveFlashSale[]>>('/pos/promotions/flash-sales/active', {
          params: { store_id: storeId },
        })
        .then((r) => unwrapPosObject(r.data)),
    evaluateCart: (data: EvaluateCartPayload): Promise<PromotionCartEvaluationDto> =>
      api
        .post<PosObjectEnvelope<PromotionCartEvaluationDto>>('/pos/promotions/evaluate', data)
        .then((r) => unwrapPosObject(r.data)),
    validateVoucher: (data: ValidateVoucherPayload): Promise<VoucherValidationResult> =>
      api
        .post<PosObjectEnvelope<VoucherValidationResult>>('/pos/promotions/vouchers/validate', data)
        .then((r) => unwrapPosObject(r.data)),
    getLoyaltyProgram: (): Promise<PosLoyaltyProgram> =>
      api
        .get<PosObjectEnvelope<PosLoyaltyProgram>>('/pos/promotions/loyalty/program')
        .then((r) => unwrapPosObject(r.data)),
    getLoyaltyBalance: (customerId: string): Promise<number> =>
      api
        .get<PosObjectEnvelope<number>>(`/pos/promotions/loyalty/balance/${customerId}`)
        .then((r) => unwrapPosObject(r.data)),
    getLoyaltyTiers: (): Promise<PosLoyaltyTier[]> =>
      api
        .get<PosObjectEnvelope<PosLoyaltyTier[]>>('/pos/promotions/loyalty/tiers')
        .then((r) => unwrapPosObject(r.data)),
    getCustomerTier: (customerId: string): Promise<PosCustomerTier> =>
      api
        .get<PosObjectEnvelope<PosCustomerTier>>(`/pos/promotions/loyalty/customer/${customerId}`)
        .then((r) => unwrapPosObject(r.data)),
  },

  // Promotions CRUD
  /**
   * TCR-2043 — phạm vi kênh B2B của user hiện tại. Route đặt TRƯỚC `v2/:id` ở
   * BE nên phải gọi qua GET riêng (không phải param) — BE cũ chưa có route
   * này sẽ rơi vào `v2/:id` và trả lỗi, caller (`usePromotionScope`) coi lỗi
   * đó là KHÔNG giới hạn.
   */
  myScope: (): Promise<PromotionViewScope> =>
    api.get(`${BASE}/v2/my-scope`).then(unwrapObject<PromotionViewScope>),
  list: (params: PromotionListParams) =>
    api.get(`${BASE}/v2`, { params }).then((r: any) => r.data),
  create: (data: PromotionPayload) =>
    api.post(`${BASE}/v2`, data).then((r: any) => r.data),
  detail: (id: string) =>
    api.get(`${BASE}/v2/${id}`).then((r: any) => r.data),
  update: (id: string, data: Partial<PromotionPayload>) =>
    api.patch(`${BASE}/v2/${id}`, data).then((r: any) => r.data),
  delete: (id: string) =>
    api.delete(`${BASE}/v2/${id}`).then((r: any) => r.data),
  activate: (id: string) =>
    api.post(`${BASE}/v2/${id}/activate`).then((r: any) => r.data),
  pause: (id: string) =>
    api.post(`${BASE}/v2/${id}/pause`).then((r: any) => r.data),
  clone: (id: string) =>
    api.post(`${BASE}/v2/${id}/clone`).then((r: any) => r.data),
  usage: (id: string) =>
    api.get(`${BASE}/v2/${id}/usage`).then((r: any) => r.data),
  evaluate: (data: EvaluateCartPayload): Promise<{ data: EvaluationResult }> =>
    api.post(`${BASE}/evaluate`, data).then((r) => r.data),

  // Vouchers
  listVouchers: (params: VoucherListParams) =>
    api.get(`${BASE}/v2/vouchers`, { params }).then((r: any) => r.data),
  createVoucher: (data: VoucherPayload) =>
    api.post(`${BASE}/v2/vouchers`, data).then((r: any) => r.data),
  detailVoucher: (id: string) =>
    api.get(`${BASE}/v2/vouchers/${id}`).then((r: any) => r.data),
  updateVoucher: (id: string, data: Partial<VoucherPayload>) =>
    api.patch(`${BASE}/v2/vouchers/${id}`, data).then((r: any) => r.data),
  deleteVoucher: (id: string) =>
    api.delete(`${BASE}/v2/vouchers/${id}`).then((r: any) => r.data),
  activateVoucher: (id: string) =>
    api.post(`${BASE}/v2/vouchers/${id}/activate`).then((r: any) => r.data),
  pauseVoucher: (id: string) =>
    api.post(`${BASE}/v2/vouchers/${id}/pause`).then((r: any) => r.data),
  validateVoucher: (data: ValidateVoucherPayload): Promise<{ data: VoucherValidationResult }> =>
    api.post(`${BASE}/vouchers/validate`, data).then((r) => r.data),
  applyVoucher: (data: { code: string; session_id: string; items: Array<{ product_id: string; quantity: number; unit_price: number }>; customer_id?: string; channel?: string }) =>
    api.post(`${BASE}/vouchers/apply`, data).then((r: any) => r.data),
  removeVoucher: (data: { code: string; session_id: string }) =>
    api.post(`${BASE}/vouchers/remove`, data).then((r: any) => r.data),
  listVoucherConditions: (voucherId: string) =>
    api.get(`${BASE}/v2/vouchers/${voucherId}/conditions`).then((r: any) => r.data),
  createVoucherCondition: (voucherId: string, data: any) =>
    api.post(`${BASE}/v2/vouchers/${voucherId}/conditions`, data).then((r: any) => r.data),
  replaceVoucherConditions: (voucherId: string, conditions: any[]) =>
    api.put(`${BASE}/v2/vouchers/${voucherId}/conditions`, { conditions }).then((r: any) => r.data),

  // Flash Sales
  getActiveFlashSales: (params?: { channel?: string; store_id?: string }) =>
    api.get(`${BASE}/v2/flash-sales/active`, { params }).then((r: any) => r.data),
  listFlashSales: (params: FlashSaleListParams) =>
    api.get(`${BASE}/v2/flash-sales`, { params }).then((r: any) => r.data),
  createFlashSale: (data: FlashSalePayload) =>
    api.post(`${BASE}/v2/flash-sales`, data).then((r: any) => r.data),
  detailFlashSale: (id: string) =>
    api.get(`${BASE}/v2/flash-sales/${id}`).then((r: any) => r.data),
  updateFlashSale: (id: string, data: Partial<FlashSalePayload>) =>
    api.patch(`${BASE}/v2/flash-sales/${id}`, data).then((r: any) => r.data),
  deleteFlashSale: (id: string) =>
    api.delete(`${BASE}/v2/flash-sales/${id}`).then((r: any) => r.data),
  activateFlashSale: (id: string) =>
    api.post(`${BASE}/v2/flash-sales/${id}/activate`).then((r: any) => r.data),
  pauseFlashSale: (id: string) =>
    api.post(`${BASE}/v2/flash-sales/${id}/pause`).then((r: any) => r.data),
  resumeFlashSale: (id: string) =>
    api.post(`${BASE}/v2/flash-sales/${id}/resume`).then((r: any) => r.data),
  restoreFlashSale: (id: string) =>
    api.post(`${BASE}/v2/flash-sales/${id}/restore`).then((r: any) => r.data),
  // TCR-1482 — clone bắt buộc nhập lại tên + thời gian (không tự sinh),
  // xem comment BE FlashSaleCrudService.clone().
  cloneFlashSale: (id: string, data: { name: string; start_time: string; end_time: string }) =>
    api.post(`${BASE}/v2/flash-sales/${id}/clone`, data).then((r: any) => r.data),
  addFlashSaleItem: (flashSaleId: string, data: AddFlashSaleItemPayload) =>
    api.post(`${BASE}/v2/flash-sales/${flashSaleId}/items`, data).then((r: any) => r.data),
  updateFlashSaleItem: (itemId: string, data: UpdateFlashSaleItemPayload) =>
    api.patch(`${BASE}/v2/flash-sales/items/${itemId}`, data).then((r: any) => r.data),
  removeFlashSaleItem: (itemId: string) =>
    api.delete(`${BASE}/v2/flash-sales/items/${itemId}`).then((r: any) => r.data),
  reserveStock: (id: string, data: { item_id: string; quantity: number; customer_id: string }) =>
    api.post(`${BASE}/v2/flash-sales/${id}/reserve`, data).then((r: any) => r.data),

  // Loyalty
  getLoyaltyProgram: () =>
    api.get(`${BASE}/v2/loyalty/program`).then((r: any) => r.data),
  updateLoyaltyProgram: (data: LoyaltyProgramPayload) =>
    api.put(`${BASE}/v2/loyalty/program`, data).then((r: any) => r.data),
  getLoyaltyBalance: (customerId: string) =>
    api.get(`${BASE}/v2/loyalty/balance/${customerId}`).then((r: any) => r.data),
  redeemPoints: (data: { customer_id: string; points: number; order_id: string }) =>
    api.post(`${BASE}/v2/loyalty/redeem`, data).then((r: any) => r.data),
  listTiers: () =>
    api.get(`${BASE}/v2/loyalty/tiers`).then((r: any) => r.data),
  createTier: (data: LoyaltyTierPayload) =>
    api.post(`${BASE}/v2/loyalty/tiers`, data).then((r: any) => r.data),
  updateTier: (id: string, data: Partial<LoyaltyTierPayload>) =>
    api.patch(`${BASE}/v2/loyalty/tiers/${id}`, data).then((r: any) => r.data),

  deleteTier: (id: string) =>
    api.delete(`${BASE}/v2/loyalty/tiers/${id}`).then((r: any) => r.data),

  // Wholesale Gifts
  listWholesaleGifts: (params: WholesaleGiftListParams) =>
    api.get(`${BASE}/v2/wholesale-gifts`, { params }).then((r: any) => r.data),
  createWholesaleGift: (data: WholesaleGiftPayload) =>
    api.post(`${BASE}/v2/wholesale-gifts`, data).then((r: any) => r.data),
  detailWholesaleGift: (id: string) =>
    api.get(`${BASE}/v2/wholesale-gifts/${id}`).then((r: any) => r.data),
  updateWholesaleGift: (id: string, data: Partial<WholesaleGiftPayload>) =>
    api.patch(`${BASE}/v2/wholesale-gifts/${id}`, data).then((r: any) => r.data),
  deleteWholesaleGift: (id: string) =>
    api.delete(`${BASE}/v2/wholesale-gifts/${id}`).then((r: any) => r.data),
  approveWholesaleGift: (id: string) =>
    api.post(`${BASE}/v2/wholesale-gifts/${id}/approve`).then((r: any) => r.data),
  reactivateWholesaleGift: (id: string) =>
    api.post(`${BASE}/v2/wholesale-gifts/${id}/reactivate`).then((r: any) => r.data),

  // Reports
  reportPerformance: (params: ReportParams) =>
    api.get(`${BASE}/v2/reports/performance`, { params }).then((r: any) => r.data),
  reportVoucherUsage: (params: ReportParams) =>
    api.get(`${BASE}/v2/reports/voucher-usage`, { params }).then((r: any) => r.data),
  reportFlashSale: (params: ReportParams) =>
    api.get(`${BASE}/v2/reports/flash-sale`, { params }).then((r: any) => r.data),
  reportLoyalty: () =>
    api.get(`${BASE}/v2/reports/loyalty`).then((r: any) => r.data),
  reportROI: (id: string) =>
    api.get(`${BASE}/v2/reports/roi/${id}`).then((r: any) => r.data),
};
